/**
 * "Click to enable sound" banner (BUILD_SPEC.md §16.1, Milestone 7 step 57).
 *
 * Modern browsers reject `<audio>.play()` until the document has
 * received a user gesture, which means a freshly-loaded dashboard
 * cannot fire its first chime — even with master mute off and the
 * right keyword toggle on. This banner makes the policy visible:
 * after 3 s of an unconfigured tab sitting on the dashboard with
 * sounds enabled, it surfaces a single-line nag asking the user to
 * click anywhere.
 *
 * The component never shows when:
 *   • the user has already interacted with the document (`isAudioUnlocked`);
 *   • the master mute is off (no sound to enable);
 *   • the 3-second grace window hasn't elapsed yet.
 *
 * The banner is dismissed implicitly by the same `markInteracted`
 * listener inside `lib/sounds.ts` — that listener fires on capture,
 * the banner's own click handler is just an accessible affordance.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Volume2 } from "lucide-react";
import { useSoundStore } from "@/stores/soundStore";
import { isAudioUnlocked, startInteractionTracking } from "@/lib/sounds";

const SHOW_AFTER_MS = 3000;
const POLL_MS = 250;

export function SoundUnlockBanner() {
  const { t } = useTranslation();
  const enabled = useSoundStore((s) => s.enabled);
  const [unlocked, setUnlocked] = useState(() => isAudioUnlocked());
  const [graceElapsed, setGraceElapsed] = useState(false);

  // Mount-time work: begin tracking interactions and poll the unlock
  // state. We don't subscribe via an event because the unlock signal
  // already lives in module-level state inside `sounds.ts` — polling
  // at 250 ms is cheaper than a custom emitter and the banner only
  // exists pre-unlock anyway.
  useEffect(() => {
    startInteractionTracking();
    const timer = window.setTimeout(() => setGraceElapsed(true), SHOW_AFTER_MS);
    const id = window.setInterval(() => {
      if (isAudioUnlocked()) {
        setUnlocked(true);
        window.clearInterval(id);
      }
    }, POLL_MS);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(id);
    };
  }, []);

  if (unlocked || !enabled || !graceElapsed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 start-1/2 z-50 -translate-x-1/2 rounded-full border border-pulse/40 dark:border-pulse-dark/40 bg-surface dark:bg-surface-dark shadow-lg px-4 py-2 text-sm text-fg dark:text-fg-dark flex items-center gap-2"
    >
      <Volume2 className="h-4 w-4 text-pulse dark:text-pulse-dark" aria-hidden />
      <span>{t("sound.unlockHint")}</span>
    </div>
  );
}
